import React from 'react';
import { 
  XMarkIcon, 
  DocumentTextIcon, 
  UserIcon, 
  CalendarIcon, 
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { Form } from '../types/Form';
import FormStatsCards from './forms/FormStatsCards';

interface FormDetailModalProps {
  form: Form;
  onClose: () => void;
}

const FormDetailModal: React.FC<FormDetailModalProps> = ({ form, onClose }) => {
  const getStatusIcon = (isActive: boolean) => {
    return isActive ? 
      <CheckCircleIcon className="w-5 h-5 text-green-500" /> : 
      <XCircleIcon className="w-5 h-5 text-red-500" />;
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 
      'bg-green-100 text-green-800' : 
      'bg-red-100 text-red-800';
  };

  const getVisibilityIcon = (isPublic: boolean) => {
    return isPublic ? 
      <GlobeAltIcon className="w-4 h-4 text-blue-500" /> : 
      <LockClosedIcon className="w-4 h-4 text-gray-500" />;
  };

  const getVisibilityColor = (isPublic: boolean) => {
    return isPublic ? 
      'bg-blue-100 text-blue-800' : 
      'bg-gray-100 text-gray-800';
  };

  const renderFormStructure = (structure: any) => {
    if (!structure || !structure.sections) return null;

    return (
      <div className="space-y-4">
        {structure.sections.map((section: any, sectionIndex: number) => (
          <div key={sectionIndex} className="border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">{section.title}</h4>
            
            {section.subsections && section.subsections.length > 0 ? (
              <div className="space-y-3">
                {section.subsections.map((subsection: any, subIndex: number) => (
                  <div key={subIndex} className="border-l-2 border-gray-100 pl-4">
                    <h5 className="text-xs font-medium text-gray-700 mb-2">{subsection.title}</h5>
                    {subsection.fields && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {subsection.fields.map((field: any, fieldIndex: number) => (
                          <div key={fieldIndex} className="text-xs text-gray-600">
                            <span className="font-medium">{field.label}</span>
                            <span className="ml-2 text-gray-500">({field.type})</span>
                            {field.required && <span className="ml-1 text-red-500">*</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : section.fields && section.fields.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {section.fields.map((field: any, fieldIndex: number) => (
                  <div key={fieldIndex} className="text-xs text-gray-600">
                    <span className="font-medium">{field.label}</span>
                    <span className="ml-2 text-gray-500">({field.type})</span>
                    {field.required && <span className="ml-1 text-red-500">*</span>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-gray-500 italic">No fields defined</div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">Form Details</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Form Information */}
            <div className="space-y-6">
              {/* Status and Visibility */}
              <div className="flex items-center space-x-4">
                <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(form.isActive)}`}>
                  {getStatusIcon(form.isActive)}
                  <span className="ml-2">{form.isActive ? 'Active' : 'Inactive'}</span>
                </span>
                <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full ${getVisibilityColor(form.isPublic)}`}>
                  {getVisibilityIcon(form.isPublic)}
                  <span className="ml-2">{form.isPublic ? 'Public' : 'Private'}</span>
                </span>
              </div>

              {/* Form Details */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{form.name}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Description</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {form.description || 'No description provided'}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Form Type</label>
                  <p className="mt-1 text-sm text-gray-900">{form.formType}</p>
                </div>

                {form.publicToken && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Public Token</label>
                    <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">
                      {form.publicToken}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Multiple Submissions</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {form.allowMultipleSubmissions ? 'Allowed' : 'Not Allowed'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Submission Count</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {form.submissionCount || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              {form.statistics && (
                <FormStatsCards statistics={form.statistics} title="Statistics" />
              )}

              {/* Creator Information */}
              {(form.creatorFirstName || form.creatorLastName) && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Created By</h4>
                  <p className="text-sm text-gray-900">
                    {form.creatorFirstName} {form.creatorLastName}
                  </p>
                </div>
              )}
            </div>

            {/* Right Column - Form Structure */}
            <div className="space-y-6">
              {/* Form Structure */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">Form Structure</h4>
                {form.formStructure ? (
                  <div className="max-h-96 overflow-y-auto">
                    {renderFormStructure(form.formStructure)}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic">No form structure defined</div>
                )}
              </div>

              {/* Timing Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Timing Information</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Created</label>
                    <p className="text-sm text-gray-900">
                      {new Date(form.createdAt).toLocaleString()}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</label>
                    <p className="text-sm text-gray-900">
                      {new Date(form.updatedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Organization Information */}
              {form.organizationName && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Organization</h4>
                  <p className="text-sm text-gray-900">{form.organizationName}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormDetailModal;
